/**
 * Paramètres délibération : choisir les périodes de paiement requises pour
 * l'accès aux résultats.
 */
import React, { useEffect, useState } from 'react';
import {
  Lock, Unlock, AlertTriangle, Save, Wrench, MessageSquare, Info,
} from 'lucide-react';
import {
  useGetPeriodesConfigQuery,
  useSetPeriodesRequisesMutation,
  useGetPublicationConfigQuery,
  useSetPublicationConfigMutation,
  useTogglePublicationMutation,
  useGetMaintenanceQuery,
  useSetMaintenanceMutation,
  useGetBlocageNotesQuery,
  useSetBlocageNotesMutation,
  useLibererBlocageNotesMutation,
  useRebloquerBlocageNotesMutation,
  useGetLimiteReclamationsQuery,
  useSetLimiteReclamationsMutation,
  CategorieFormation,
} from '../../services/portailAdminApi';
import { useGetPolesQuery, useGetClassesQuery } from '../../services/polesOptionsApi';

const LimiteReclamationsCard: React.FC = () => {
  const { data, refetch } = useGetLimiteReclamationsQuery();
  const [setMut, { isLoading }] = useSetLimiteReclamationsMutation();
  const [valeur, setValeur] = useState<number>(10);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (data?.limite) setValeur(data.limite);
  }, [data]);

  const save = async () => {
    setMessage('');
    try {
      const res = await setMut({ limite: valeur }).unwrap();
      setMessage(`✓ Limite mise à jour : ${res.limite} réclamations / semestre`);
      refetch();
      setTimeout(() => setMessage(''), 4000);
    } catch (e: any) {
      setMessage('Erreur : ' + (e?.data?.detail || ''));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
      <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
        <MessageSquare size={18} className="text-blue-600" />
        Limite de réclamations par étudiant
      </h2>
      <p className="text-xs text-slate-500 dark:text-gray-400 mb-3">
        Nombre maximum de réclamations qu'un étudiant peut déposer par semestre.
        Au-delà, un message de blocage s'affiche.
      </p>
      <div className="flex items-end gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-gray-400 mb-1">
            Limite (1 – 50)
          </label>
          <input
            type="number"
            min="1"
            max="50"
            value={valeur}
            onChange={(e) => setValeur(parseInt(e.target.value) || 1)}
            className="w-28 px-3 py-1.5 border border-slate-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-slate-900 dark:text-white text-sm"
          />
        </div>
        <button
          onClick={save}
          disabled={isLoading || valeur === data?.limite}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-xs font-semibold"
        >
          <Save size={12} />
          {isLoading ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {data?.limite && (
          <span className="text-xs text-slate-500 dark:text-gray-500 ml-1 mb-1">
            Actuelle : <strong className="text-slate-700 dark:text-gray-300">{data.limite}</strong>
          </span>
        )}
      </div>
      {message && (
        <div className={`mt-3 text-xs px-2 py-1.5 rounded ${
          message.startsWith('✓')
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message.replace(/^✓ /, '')}
        </div>
      )}
    </div>
  );
};

const MaintenanceCard: React.FC = () => {
  const { data, isLoading, refetch } = useGetMaintenanceQuery();
  const [setMut, { isLoading: saving }] = useSetMaintenanceMutation();
  const [message, setMessage] = useState('');
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    if (data) setMessage(data.message || '');
  }, [data]);

  const activer = async () => {
    if (!window.confirm(
      'ACTIVER le mode maintenance ?\n\n'
      + 'Tous les étudiants verront une page de maintenance et ne pourront pas se connecter '
      + 'au portail. Les sessions actives seront aussi bloquées au prochain appel API.\n\n'
      + 'N\'oubliez pas de désactiver après vos opérations.'
    )) return;
    try {
      await setMut({ actif: true, message: message.trim() || 'Le portail est temporairement en maintenance. Réessayez dans quelques minutes.' }).unwrap();
      refetch();
      setEdit(false);
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const desactiver = async () => {
    if (!window.confirm('Désactiver le mode maintenance ? Le portail redevient accessible.')) return;
    try {
      await setMut({ actif: false }).unwrap();
      refetch();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const updateMessage = async () => {
    try {
      await setMut({ actif: true, message: message.trim() }).unwrap();
      refetch();
      setEdit(false);
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  if (isLoading || !data) return null;

  return (
    <div className={`rounded-lg shadow border p-5 ${
      data.actif
        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            🔧 Mode maintenance
            {data.actif && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
                ACTIF
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Bloque temporairement l'accès des étudiants pendant les opérations sensibles
            (import de notes, calcul des moyennes, modifications de structure...).
          </p>
        </div>
      </div>

      {data.actif && (
        <div className="mb-3 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-xs">
          <div className="font-semibold text-red-800 dark:text-red-200">⚠ Maintenance en cours</div>
          <div className="text-red-700 dark:text-red-300 mt-1">
            Activé {data.depuis && `le ${new Date(data.depuis).toLocaleString('fr-FR')}`}
            {data.par_nom && ` par ${data.par_nom}`}
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message affiché aux étudiants
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={saving || (data.actif && !edit)}
          rows={2}
          maxLength={500}
          placeholder="Ex: Maintenance en cours, retour prévu dans 30 minutes..."
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
        />
        <div className="text-xs text-gray-400 mt-0.5">{message.length}/500</div>
      </div>

      <div className="flex gap-2">
        {data.actif ? (
          <>
            {edit ? (
              <button
                onClick={updateMessage}
                disabled={saving}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-sm font-medium rounded"
              >
                💾 Enregistrer le message
              </button>
            ) : (
              <button
                onClick={() => setEdit(true)}
                className="px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-sm rounded"
              >
                ✏️ Modifier le message
              </button>
            )}
            <button
              onClick={desactiver}
              disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-bold rounded"
            >
              {saving ? '...' : '✓ DÉSACTIVER LA MAINTENANCE'}
            </button>
          </>
        ) : (
          <button
            onClick={activer}
            disabled={saving}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-bold rounded"
          >
            {saving ? '...' : '🔒 ACTIVER LA MAINTENANCE'}
          </button>
        )}
      </div>
    </div>
  );
};

const DEFAULT_MSG_BLOCAGE = "Délibération des examens en cours. Vos notes seront disponibles très prochainement.";


// ═══════════════════════════════════════════════════════════════════════════
// CENTRE DE VERROUS (fix 2026-09-06 v2) — TOUS les verrous en un endroit,
// groupés par domaine (consultation / scans / saisie), avec toggles par ligne
// et 2 boutons "Démarrer/Terminer délibération S1/S2" qui verrouillent tout
// le semestre concerné d'un coup.
// ═══════════════════════════════════════════════════════════════════════════

type CentreVerrouItem = {
  cle: string;
  cle_param: string;
  domaine: 'consultation' | 'scans' | 'saisie';
  label: string;
  description: string;
  impact: string;
  actif: boolean;
  inverse?: boolean;
};

const DOMAINE_META: Record<string, { titre: string; description: string; couleur: string }> = {
  consultation: {
    titre: 'Consultation portail (étudiants & parents)',
    description: 'Contrôle ce que les étudiants peuvent voir sur leur portail. Ne bloque pas la saisie côté agents.',
    couleur: 'blue',
  },
  scans: {
    titre: 'Scans copies (étudiants & parents)',
    description: 'Contrôle l\'accès aux images/PDF des copies scannées. Notes chiffrées restent visibles selon leur propre verrou.',
    couleur: 'purple',
  },
  saisie: {
    titre: 'Saisie des notes (agents & formateurs)',
    description: 'Contrôle la capacité des agents/formateurs à modifier les notes. N\'affecte pas la consultation portail.',
    couleur: 'orange',
  },
};

const CentreVerrousCard: React.FC = () => {
  const [verrous, setVerrous] = React.useState<CentreVerrouItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState<string | null>(null);
  const [msg, setMsg] = React.useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  // Réservé aux admins ou aux agents autorisés (2026-10-05) : masqué sans droit,
  // lecture seule sans droit de modification.
  const [refuse, setRefuse] = React.useState(false);
  const [peutModifier, setPeutModifier] = React.useState(false);

  const authHeaders = React.useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('access_token') || ''}`,
  }), []);

  const charger = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/portail/admin/centre-verrous/', { headers: authHeaders() });
      if (r.status === 403) {
        setRefuse(true);
      } else if (r.ok) {
        const j = await r.json();
        setVerrous(j.verrous || []);
        setPeutModifier(j.peut_modifier !== false);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [authHeaders]);
  React.useEffect(() => { charger(); }, [charger]);

  const toggle = async (v: CentreVerrouItem) => {
    setSaving(v.cle); setMsg(null);
    try {
      const r = await fetch('/api/portail/admin/centre-verrous/toggle/', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cle: v.cle, actif: !v.actif }),
      });
      if (!r.ok) throw new Error((await r.json())?.detail || 'Erreur');
      const updated = await r.json();
      setVerrous(prev => prev.map(x => x.cle === updated.cle ? updated : x));
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Erreur' });
    } finally { setSaving(null); }
  };

  const bulkSemestre = async (semestre: 'S1' | 'S2', actif: boolean) => {
    const label = semestre === 'S1' ? 'impair (S1/S3/S5)' : 'pair (S2/S4/S6)';
    const action = actif ? 'VERROUILLER' : 'LIBÉRER';
    if (!window.confirm(
      `${action} d'un coup tous les verrous du semestre ${label} ?\n\n` +
      `Cela ${actif ? 'active' : 'désactive'} :\n` +
      `  • Consultation relevés ${label}\n` +
      `  • Masquage notes examens ${label}\n` +
      `  • Saisie devoirs ${label}\n` +
      `  • Saisie examens ${label}\n` +
      `  • Saisie rattrapage ${label}\n`
    )) return;
    setSaving(`sem-${semestre}`); setMsg(null);
    try {
      const r = await fetch('/api/portail/admin/centre-verrous/semestre/', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ semestre, actif }),
      });
      if (!r.ok) throw new Error((await r.json())?.detail || 'Erreur');
      const j = await r.json();
      setMsg({
        type: 'ok',
        text: `${j.modifies.length} verrou(s) ${actif ? 'activé(s)' : 'désactivé(s)'} pour le semestre ${label}.`,
      });
      await charger();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Erreur' });
    } finally { setSaving(null); }
  };

  if (refuse) return null;

  const nbActifs = verrous.filter(v => v.actif).length;
  const grouped: Record<string, CentreVerrouItem[]> = { consultation: [], scans: [], saisie: [] };
  for (const v of verrous) (grouped[v.domaine] ||= []).push(v);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Lock size={18} className="text-slate-600" />
            Centre de Verrous
            {loading ? (
              <span className="text-xs text-slate-400">Chargement…</span>
            ) : nbActifs > 0 ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 font-semibold">
                {nbActifs} actif{nbActifs > 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 font-semibold">
                Aucun blocage
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
            {!peutModifier && !loading && (
              <span className="block mb-1 font-semibold text-amber-700 dark:text-amber-400">
                Lecture seule : votre compte peut consulter les verrous, pas les modifier.
              </span>
            )}
            Tous les verrous du portail centralisés ici. Chaque interrupteur agit
            immédiatement. Les 2 boutons ci-dessous verrouillent tout un semestre
            d'un coup (recommandé pendant la délibération).
          </p>
        </div>
      </div>

      {/* Boutons bulk semestre */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {(['S1', 'S2'] as const).map(sem => {
          const label = sem === 'S1' ? 'impairs (S1/S3/S5)' : 'pairs (S2/S4/S6)';
          const cles = [
            `consultation_releves_${sem.toLowerCase()}`,
            `consultation_examens_${sem.toLowerCase()}`,
            `saisie_devoirs_${sem.toLowerCase()}`,
            `saisie_examens_${sem.toLowerCase()}`,
            `saisie_rattrapage_${sem.toLowerCase()}`,
          ];
          const actifs = verrous.filter(v => cles.includes(v.cle) && v.actif).length;
          const total = cles.length;
          const semKey = `sem-${sem}`;
          return (
            <div key={sem} className={`rounded-lg border p-3 ${
              actifs === total && total > 0
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
                : 'bg-slate-50 dark:bg-gray-900/40 border-slate-200 dark:border-gray-700'
            }`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-bold text-slate-800 dark:text-gray-200">
                    Délibération semestres {label}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-gray-400 mt-0.5">
                    {actifs}/{total} verrous du semestre actifs
                  </div>
                </div>
                {actifs === total && total > 0 ? (
                  <button
                    onClick={() => bulkSemestre(sem, false)}
                    disabled={saving === semKey || !peutModifier}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg whitespace-nowrap"
                  >
                    <Unlock size={12} /> Terminer
                  </button>
                ) : (
                  <button
                    onClick={() => bulkSemestre(sem, true)}
                    disabled={saving === semKey || !peutModifier}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg whitespace-nowrap"
                  >
                    <Lock size={12} /> Démarrer
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {msg && (
        <div className={`mb-3 p-2 rounded-lg text-xs ${
          msg.type === 'ok'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>{msg.text}</div>
      )}

      {/* Groupes de verrous individuels */}
      {(['consultation', 'scans', 'saisie'] as const).map(dom => (
        <div key={dom} className="mb-4 last:mb-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-gray-400 mb-1">
            {DOMAINE_META[dom].titre}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-gray-500 mb-2">
            {DOMAINE_META[dom].description}
          </p>
          <div className="space-y-1.5">
            {grouped[dom].map(v => (
              <div key={v.cle}
                className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 ${
                  v.actif
                    ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                    : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {v.actif ? (
                      <Lock size={12} className="text-red-600 flex-shrink-0" />
                    ) : (
                      <Unlock size={12} className="text-slate-400 flex-shrink-0" />
                    )}
                    <div className="text-xs font-semibold text-slate-800 dark:text-gray-200">
                      {v.label}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 dark:text-gray-500 mt-0.5 ml-5">
                    {v.description}
                  </div>
                </div>
                <button
                  onClick={() => toggle(v)}
                  disabled={saving === v.cle || !peutModifier}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-md whitespace-nowrap flex-shrink-0 ${
                    v.actif
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {saving === v.cle ? '…' : v.actif ? 'Débloquer' : 'Verrouiller'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};


// Fix 2026-09-06 : synthèse des verrous portail actifs — repérer les
// confrontations (2 verrous simultanés sur le même sujet ex. blocage_notes
// + verrou parité). Sert de tableau de bord "État du portail" en haut de la
// page Paramètres.
const VerrousSyntheseCard: React.FC = () => {
  const [synth, setSynth] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);

  const charger = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/portail/admin/verrous-synthese/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('access_token') || ''}` },
      });
      if (r.ok) setSynth(await r.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { charger(); }, [charger]);

  if (loading || !synth) return null;

  const total = synth.total_verrous_actifs || 0;
  const b = synth.blocage_notes || {};
  const m = synth.maintenance || {};
  const relParite = synth.releves_verrous_parite || [];
  const scansParite = synth.scans_verrous_parite || [];
  const mx = synth.masquer_examens || {};
  const sn = synth.saisie_notes || {};

  // Détection de confrontations
  const confrontations: string[] = [];
  // Confrontation A : blocage_notes actif SANS libération + verrou parité qui
  // couvrirait le même semestre → double blocage inutile.
  if (b.actif && b.nb_liberations === 0 && relParite.length > 0) {
    confrontations.push(
      `Blocage global actif ET verrou parité relevé ${relParite.map((r: any) => r.semestre).join('/')} — ` +
      `l'un des deux suffit. Le blocage global couvre déjà tout.`
    );
  }
  // Confrontation B : maintenance active + blocage_notes actif
  if (m.actif && b.actif) {
    confrontations.push(
      'Maintenance ET blocage notes actifs simultanément — la maintenance ' +
      'coupe déjà tout le portail. Blocage notes redondant.'
    );
  }
  // Confrontation C : masquer_examens actif ET blocage_notes actif
  if ((mx.global || mx.s1 || mx.s2) && b.actif && b.nb_liberations === 0) {
    confrontations.push(
      'Masquage examens actif ET blocage global des notes — blocage total ' +
      'couvre déjà les examens. Un seul suffit.'
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Lock size={18} className="text-slate-500" />
            État des verrous portail
            {total > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-semibold">
                {total} actif{total > 1 ? 's' : ''}
              </span>
            )}
            {total === 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-semibold">
                Aucun blocage
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
            Synthèse des verrouillages qui peuvent bloquer la consultation portail.
            Sert à éviter les confrontations (ex : blocage global + verrou parité).
          </p>
        </div>
        <button onClick={charger} className="text-xs text-slate-500 hover:text-slate-700">Actualiser</button>
      </div>

      {confrontations.length > 0 && (
        <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
          <div className="flex items-center gap-1.5 text-amber-800 dark:text-amber-200 text-xs font-semibold mb-1">
            <AlertTriangle size={12} /> Configuration redondante détectée
          </div>
          <ul className="list-disc list-inside space-y-1 text-[11px] text-amber-700 dark:text-amber-300">
            {confrontations.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      {total === 0 ? (
        <div className="text-xs text-slate-500 dark:text-gray-500 italic">
          Aucun verrou actif — les étudiants et parents peuvent tout consulter (sous réserve
          d'être à jour des paiements).
        </div>
      ) : (
        <ul className="space-y-1.5 text-xs">
          {b.actif && (
            <li className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
              <Lock size={12} className="text-orange-600" />
              <span className="font-semibold text-orange-800 dark:text-orange-300">Blocage global consultation notes</span>
              <span className="text-orange-700 dark:text-orange-400">
                {b.nb_liberations > 0
                  ? `— ${b.nb_liberations} libération${b.nb_liberations > 1 ? 's' : ''} active${b.nb_liberations > 1 ? 's' : ''}`
                  : '— aucune libération (blocage total)'}
              </span>
              {b.par_nom && <span className="text-[10px] text-orange-600 ml-auto">par {b.par_nom}</span>}
            </li>
          )}
          {m.actif && (
            <li className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <Wrench size={12} className="text-red-600" />
              <span className="font-semibold text-red-800 dark:text-red-300">Maintenance</span>
              <span className="text-red-700 dark:text-red-400 truncate">— {m.message || '—'}</span>
            </li>
          )}
          {!synth.scans_actif && (
            <li className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700">
              <Lock size={12} className="text-slate-600" />
              <span className="font-semibold text-slate-800 dark:text-gray-200">Consultation des scans copies : bloquée</span>
            </li>
          )}
          {relParite.map((r: any, i: number) => (
            <li key={`rel-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700">
              <Lock size={12} className="text-slate-600" />
              <span className="font-semibold text-slate-800 dark:text-gray-200">Verrou relevés {r.semestre === 'S1' ? 'impairs (S1/S3/S5)' : 'pairs (S2/S4/S6)'}</span>
            </li>
          ))}
          {scansParite.map((s: any, i: number) => (
            <li key={`sc-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700">
              <Lock size={12} className="text-slate-600" />
              <span className="font-semibold text-slate-800 dark:text-gray-200">
                Verrou scans {s.semestre === 'S1' ? 'S1/S3/S5' : 'S2/S4/S6'} · session {s.session}
              </span>
            </li>
          ))}
          {(mx.global || mx.s1 || mx.s2) && (
            <li className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700">
              <Info size={12} className="text-slate-600" />
              <span className="font-semibold text-slate-800 dark:text-gray-200">
                Masquage notes examens : {mx.global ? 'tous semestres' : [mx.s1 && 'S1/S3/S5', mx.s2 && 'S2/S4/S6'].filter(Boolean).join(' + ')}
              </span>
              <span className="text-[10px] text-slate-500">(devoirs restent visibles)</span>
            </li>
          )}
          {(sn.global || sn.s1 || sn.s2) && (
            <li className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-gray-900/40 border border-slate-200 dark:border-gray-700">
              <Lock size={12} className="text-slate-600" />
              <span className="font-semibold text-slate-800 dark:text-gray-200">
                Verrou saisie notes : {sn.global ? 'tous semestres' : [sn.s1 && 'S1/S3/S5', sn.s2 && 'S2/S4/S6'].filter(Boolean).join(' + ')}
              </span>
              <span className="text-[10px] text-slate-500">(agents ne peuvent plus saisir)</span>
            </li>
          )}
        </ul>
      )}

      {b.actif && b.nb_liberations > 0 && relParite.length > 0 && (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg text-[11px] text-blue-800 dark:text-blue-200">
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <Info size={11} /> Règle de précédence appliquée
          </div>
          Si un étudiant est libéré explicitement (par une libération pôle/semestre)
          il OUTREPASSE le verrou parité. Sinon le verrou parité s'applique normalement.
        </div>
      )}
    </div>
  );
};

const BlocageNotesCard: React.FC = () => {
  const { data, isLoading, refetch } = useGetBlocageNotesQuery();
  const [setMut, { isLoading: saving }] = useSetBlocageNotesMutation();
  const [liberer, { isLoading: liberating }] = useLibererBlocageNotesMutation();
  const [rebloquer, { isLoading: rebloquing }] = useRebloquerBlocageNotesMutation();
  const { data: poles = [] } = useGetPolesQuery({});
  const { data: classes = [] } = useGetClassesQuery({ actif: true });
  const [message, setMessage] = useState('');
  const [edit, setEdit] = useState(false);
  // Form ajout libération. Fix 2026-09-07 : semestre RÉINTRODUIT comme
  // filtre facultatif (au moins pole/niveau/classe requis en plus).
  const [newPole, setNewPole] = useState('');
  const [newNiveau, setNewNiveau] = useState('');
  const [newClasse, setNewClasse] = useState('');
  const [newSemestre, setNewSemestre] = useState<'' | 'S1' | 'S2'>('');

  const handleAjouterLiberation = async () => {
    if (!newPole && !newNiveau && !newClasse) {
      alert('Sélectionnez au moins un critère de groupe (pôle, niveau ou classe).\n\n' +
            'Le semestre seul n\'est pas accepté — utilisez le Centre de Verrous pour verrouiller un semestre entier.');
      return;
    }
    try {
      await liberer({
        pole: newPole || undefined,
        niveau: newNiveau || undefined,
        classe: newClasse || undefined,
        semestre: newSemestre || undefined,
      } as any).unwrap();
      setNewPole(''); setNewNiveau(''); setNewClasse(''); setNewSemestre('');
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const handleRetirerLiberation = async (lib: { pole: string; niveau: string; classe: string; semestre?: string }) => {
    const libelle = [
      lib.pole && `pôle « ${lib.pole} »`,
      lib.niveau && `niveau ${lib.niveau}`,
      lib.classe && `classe ${lib.classe}`,
      lib.semestre && `semestre ${lib.semestre === 'S1' ? 'impair' : 'pair'}`,
    ].filter(Boolean).join(' / ');
    if (!window.confirm(`Re-bloquer la libération ${libelle} ?\n\nLes étudiants concernés ne verront PLUS leurs notes jusqu'à la prochaine libération.`)) return;
    try {
      await rebloquer({
        pole: lib.pole || undefined,
        niveau: lib.niveau || undefined,
        classe: lib.classe || undefined,
        semestre: lib.semestre || undefined,
      } as any).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  useEffect(() => {
    if (data) setMessage(data.message || DEFAULT_MSG_BLOCAGE);
  }, [data]);

  const activer = async () => {
    if (!window.confirm(
      'ACTIVER le blocage de consultation des notes ?\n\n'
      + 'Les étudiants et les parents ne pourront PLUS consulter les relevés '
      + 'pendant la durée du blocage. Le reste du portail (chat, anniversaires, '
      + 'notifications, etc.) reste accessible normalement.\n\n'
      + 'À désactiver dès que la délibération sera terminée.'
    )) return;
    try {
      await setMut({ actif: true, message: message.trim() || DEFAULT_MSG_BLOCAGE }).unwrap();
      refetch();
      setEdit(false);
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const desactiver = async () => {
    if (!window.confirm(
      'Libérer l\'accès aux notes ?\n\n'
      + 'Les étudiants et les parents pourront à nouveau consulter les relevés.'
    )) return;
    try {
      await setMut({ actif: false }).unwrap();
      refetch();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const updateMessage = async () => {
    try {
      await setMut({ actif: true, message: message.trim() }).unwrap();
      refetch();
      setEdit(false);
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  if (isLoading || !data) return null;

  return (
    <div className={`rounded-lg shadow border p-5 ${
      data.actif
        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            Blocage de consultation des notes
            {data.actif && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-600 text-white">
                ACTIF
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Bloque temporairement la consultation des relevés côté <strong>étudiants ET parents</strong>
            {' '}pendant la délibération finale (ex : rattrapages). Le reste du portail reste accessible.
          </p>
        </div>
      </div>

      {data.actif && (
        <div className="mb-3 p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded text-xs">
          <div className="font-semibold text-amber-800 dark:text-amber-200">Délibération en cours</div>
          <div className="text-amber-700 dark:text-amber-300 mt-1">
            Activé {data.depuis && `le ${new Date(data.depuis).toLocaleString('fr-FR')}`}
            {data.par_nom && ` par ${data.par_nom}`}
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message affiché à la place des notes
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={saving || (data.actif && !edit)}
          rows={2}
          maxLength={500}
          placeholder={DEFAULT_MSG_BLOCAGE}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
        />
        <div className="text-xs text-gray-400 mt-0.5">{message.length}/500</div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {data.actif ? (
          <>
            {edit ? (
              <button
                onClick={updateMessage}
                disabled={saving}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded"
              >
                Enregistrer le message
              </button>
            ) : (
              <button
                onClick={() => setEdit(true)}
                className="px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm rounded"
              >
                Modifier le message
              </button>
            )}
            <button
              onClick={desactiver}
              disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-bold rounded"
            >
              {saving ? '...' : 'DÉSACTIVER COMPLÈTEMENT'}
            </button>
          </>
        ) : (
          <button
            onClick={activer}
            disabled={saving}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-bold rounded"
          >
            {saving ? '...' : 'BLOQUER LES NOTES'}
          </button>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* Libérations progressives : libérer pôle par pôle / niveau par niveau */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* Fix 2026-09-17 : la rubrique restait invisible quand le blocage était
          désactivé — les agents croyaient l'avoir perdue. Elle est désormais
          toujours affichée, avec un rappel de son effet. */}
      {(
        <div className="mt-5 pt-4 border-t border-amber-300 dark:border-amber-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
            Délibération progressive — libérer par pôle / niveau / classe
          </h3>
          {!data.actif && (
            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded text-xs text-blue-800 dark:text-blue-200">
              <strong>Blocage des notes désactivé</strong> — tous les étudiants voient
              actuellement leurs notes. Les libérations ci-dessous sont conservées et ne
              prendront effet qu'après un clic sur « BLOQUER LES NOTES ».
            </div>
          )}
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Quand le blocage est actif, vous pouvez libérer l'accès aux notes
            <strong> pour un sous-ensemble d'étudiants seulement</strong>. Exemple :
            terminez d'abord la délibération du pôle Commerce → libérez Commerce →
            les étudiants Commerce voient leurs notes pendant que les autres restent bloqués.
            Pour verrouiller/libérer un semestre entier (impair ou pair), utilisez plutôt
            le <strong>« Centre de Verrous »</strong> ci-dessus. Les champs vides agissent comme un joker
            (laisser tout vide sur pôle ET niveau ET classe = non autorisé).
          </p>

          {/* Liste des libérations actuelles */}
          {data.liberations && data.liberations.length > 0 ? (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Libérations actives ({data.liberations.length})
              </div>
              <ul className="space-y-1.5">
                {data.liberations.map((lib: any, idx) => (
                  <li key={idx} className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded px-2.5 py-1.5">
                    <span className="text-emerald-700 dark:text-emerald-300 text-xs font-semibold">✓ Libéré</span>
                    <span className="text-xs text-gray-700 dark:text-gray-300 flex-1">
                      {lib.pole && <span className="inline-block bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded mr-1">Pôle : <strong>{lib.pole}</strong></span>}
                      {lib.niveau && <span className="inline-block bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded mr-1">Niveau : <strong>{lib.niveau}</strong></span>}
                      {lib.classe && <span className="inline-block bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded mr-1">Classe : <strong>{lib.classe}</strong></span>}
                      {lib.semestre && (
                        <span className="inline-block bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded mr-1 text-indigo-700 dark:text-indigo-300">
                          Semestre : <strong>{lib.semestre === 'S1' ? 'impair (S1/S3/S5)' : 'pair (S2/S4/S6)'}</strong>
                        </span>
                      )}
                    </span>
                    <button
                      onClick={() => handleRetirerLiberation(lib)}
                      disabled={rebloquing}
                      title="Re-bloquer ce groupe"
                      className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded disabled:opacity-50"
                    >
                      Re-bloquer
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-xs text-red-800 dark:text-red-200">
              <strong>Aucune libération active</strong> — tous les étudiants sont actuellement bloqués.
            </div>
          )}

          {/* Formulaire ajout libération */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-3">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Ajouter une libération
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
              <div>
                <label className="block text-[10px] uppercase font-medium text-gray-500 mb-0.5">Pôle</label>
                <select
                  value={newPole}
                  onChange={(e) => setNewPole(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">— tous —</option>
                  {(poles as any[]).map((p: any) => (
                    <option key={p.id} value={p.nom}>{p.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase font-medium text-gray-500 mb-0.5">Niveau</label>
                <select
                  value={newNiveau}
                  onChange={(e) => setNewNiveau(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">— tous —</option>
                  {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase font-medium text-gray-500 mb-0.5">Classe</label>
                <select
                  value={newClasse}
                  onChange={(e) => setNewClasse(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">— toutes —</option>
                  {(classes as any[])
                    .filter((c: any) => !newNiveau || c.niveau === newNiveau)
                    .map((c: any) => (
                      <option key={c.id} value={c.code}>{c.code}</option>
                    ))}
                </select>
              </div>
              {/* Fix 2026-09-07 : sélecteur semestre (filtre facultatif) */}
              <div>
                <label className="block text-[10px] uppercase font-medium text-gray-500 mb-0.5">Semestre</label>
                <select
                  value={newSemestre}
                  onChange={(e) => setNewSemestre(e.target.value as '' | 'S1' | 'S2')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">— les 2 —</option>
                  <option value="S1">Impair (S1/S3/S5)</option>
                  <option value="S2">Pair (S2/S4/S6)</option>
                </select>
              </div>
              <button
                onClick={handleAjouterLiberation}
                disabled={liberating || (!newPole && !newNiveau && !newClasse)}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold rounded"
              >
                {liberating ? '...' : 'Libérer ce groupe'}
              </button>
            </div>
            <div className="text-[10px] text-gray-500 mt-2">
              <strong>Au moins un critère de groupe</strong> (pôle, niveau ou classe) est requis.
              Le semestre est un filtre <em>optionnel</em> — exemple : libérer <strong>Commerce · S2 pair</strong>
              affiche les S2 aux étudiants Commerce tout en gardant leurs S1 bloqués.
              Pour verrouiller un semestre entier pour <strong>tous</strong> les étudiants, utilisez plutôt
              le « Centre de Verrous » en haut.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export const Parametres: React.FC = () => {
  const { data, isLoading, refetch } = useGetPeriodesConfigQuery();
  const [setMut, { isLoading: saving }] = useSetPeriodesRequisesMutation();

  const [selection, setSelection] = useState<Set<string>>(new Set());
  // Accès léger (2026-09-12) : périodes dont l'impayé laisse voir la décision sans les notes
  const [legeres, setLegeres] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Type de formation configuré (2026-10-03) : FI, cours du soir et master ont chacun leurs périodes
  const [categorie, setCategorie] = useState<CategorieFormation>('FI');
  const configCategorie = data?.categories?.find((c) => c.code === categorie);

  const charger = () => {
    if (!data) return;
    setSelection(new Set(configCategorie ? configCategorie.periodes_requises : data.periodes_requises));
    setLegeres(new Set(configCategorie ? configCategorie.periodes_acces_leger : (data.periodes_acces_leger || [])));
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, categorie]);

  const toggle = (code: string) => {
    const retrait = selection.has(code);
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    if (retrait) {
      setLegeres((prev) => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
    }
  };

  const toggleLeger = (code: string) => {
    setLegeres((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const save = async () => {
    setMessage(null);
    try {
      const res = await setMut({
        periodes: Array.from(selection),
        periodes_acces_leger: Array.from(legeres).filter((c) => selection.has(c)),
        categorie,
      }).unwrap();
      const legeresRes = res.periodes_acces_leger || [];
      const lib = configCategorie?.label || categorie;
      setMessage({
        type: 'success',
        text: (res.periodes_requises.length === 0
          ? `${lib} : configuration enregistrée. Aucune période requise — ces étudiants pourront tous voir leurs résultats.`
          : `${lib} : configuration enregistrée. ${res.periodes_requises.length} périodes actives : ${res.periodes_requises.join(', ')}`)
          + (legeresRes.length ? ` — accès léger : ${legeresRes.join(', ')}` : ''),
      });
      refetch();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.data?.detail || 'Erreur lors de la sauvegarde' });
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">Chargement...</div>;
  }

  if (!data) {
    return <div className="p-8 text-center text-red-500">Impossible de charger la configuration</div>;
  }

  const dispo = data.periodes_disponibles;

  return (
    <div className="space-y-6">
      {/* Fix 2026-09-06 v2 : Centre de Verrous unifié - toggle tout ici */}
      <CentreVerrousCard />
      <MaintenanceCard />
      <BlocageNotesCard />
      <LimiteReclamationsCard />
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
        <p className="font-semibold mb-1">Comment ça marche ?</p>
        <p>
          Cochez les périodes de paiement obligatoires pour qu'un étudiant accède à ses résultats.
          Si toutes ses périodes cochées sont à jour, il voit son relevé. Sinon, il voit un écran
          de blocage avec le détail de ses dettes.
        </p>
        <p className="mt-2">
          <span className="font-semibold">Par type de formation</span> : la formation initiale (FI / DD), les cours
          du soir et les masters ont chacun leurs périodes. Un type jamais enregistré reprend la liste générale.
        </p>
        <p className="mt-2">
          <span className="font-semibold">Accès léger</span> : sur une période cochée, activez « accès léger »
          pour que les étudiants qui ne doivent QUE ces périodes voient s'ils ont validé ou non
          (et les matières à rattraper par UE), sans aucune note ni moyenne.
        </p>
        <p className="mt-2 text-xs opacity-80">
          Vous pouvez modifier cette configuration à tout moment, l'effet est immédiat.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Périodes requises pour accéder aux résultats
        </h2>

        {(data.categories || []).length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Type de formation">
              {(data.categories || []).map((c) => (
                <button
                  key={c.code}
                  type="button"
                  role="tab"
                  aria-selected={categorie === c.code}
                  onClick={() => { setCategorie(c.code); setMessage(null); }}
                  className={`px-3 py-2 rounded-lg border text-sm text-left ${
                    categorie === c.code
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-semibold'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  {c.label}
                  <span className="ml-2 text-xs font-normal text-gray-500">{c.nb_etudiants} étudiant(s)</span>
                </button>
              ))}
            </div>
            {configCategorie && !configCategorie.configuree && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                Ce type n'a pas encore sa propre liste : il reprend la liste générale ci-dessous.
                Enregistrez pour lui donner ses propres périodes.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {dispo.map((p) => {
            const checked = selection.has(p.code);
            const leger = checked && legeres.has(p.code);
            return (
              <div
                key={p.code}
                className={`p-3 rounded-lg border transition-colors ${
                  checked
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(p.code)}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <div>
                    <div className="font-mono text-xs text-gray-500 dark:text-gray-400">{p.code}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{p.label}</div>
                  </div>
                </label>
                {checked && (
                  <label
                    className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800 flex items-center gap-2 cursor-pointer text-xs text-gray-700 dark:text-gray-300"
                    title="Les étudiants qui ne doivent que les périodes en accès léger voient leur décision, sans notes ni moyennes"
                  >
                    <input
                      type="checkbox"
                      checked={leger}
                      onChange={() => toggleLeger(p.code)}
                      className="w-4 h-4 rounded"
                    />
                    <span className={leger ? 'font-semibold text-sky-700 dark:text-sky-300' : ''}>
                      Accès léger {leger ? '(activé)' : ''}
                    </span>
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={charger}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : `Enregistrer — ${configCategorie?.label || 'liste générale'}`}
          </button>
        </div>
      </div>

      <PublicationConfig />
    </div>
  );
};

const PublicationConfig: React.FC = () => {
  const { data, refetch } = useGetPublicationConfigQuery();
  const [setMut, { isLoading: saving }] = useSetPublicationConfigMutation();
  const [toggleMut, { isLoading: toggling }] = useTogglePublicationMutation();
  const [datePub, setDatePub] = useState('');
  const [jours, setJours] = useState('7');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const toggleReclamations = async () => {
    setMsg(null);
    try {
      const res = await toggleMut().unwrap();
      setMsg({
        type: 'success',
        text: res.action === 'ouvertes'
          ? 'Réclamations OUVERTES — les étudiants peuvent maintenant déposer des réclamations.'
          : 'Réclamations FERMÉES — les étudiants ne peuvent plus déposer de réclamations.',
      });
      refetch();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.data?.detail || 'Erreur' });
    }
  };

  React.useEffect(() => {
    if (data) {
      setDatePub(data.fenetre?.date_publication?.slice(0, 16) || '');
      setJours(String(data.jours_reclamation || 7));
    }
  }, [data]);

  const save = async () => {
    setMsg(null);
    try {
      await setMut({
        date_publication: datePub,
        jours_reclamation: parseInt(jours) || 7,
      }).unwrap();
      setMsg({ type: 'success', text: 'Configuration sauvegardée' });
      refetch();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.data?.detail || 'Erreur' });
    }
  };

  const fenetre = data?.fenetre;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
      <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
        Publication des résultats & fenêtre de réclamation
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Définit la date à partir de laquelle les étudiants peuvent déposer une réclamation, et la durée pendant laquelle c'est possible.
      </p>

      {/* Toggle rapide ouvert/fermé */}
      <div className="mb-5 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-gray-900 dark:text-white text-sm">
            État des réclamations : {fenetre?.ouvertes ? (
              <span className="text-green-700 dark:text-green-400">OUVERTES</span>
            ) : (
              <span className="text-red-700 dark:text-red-400">FERMÉES</span>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {fenetre?.ouvertes
              ? `${fenetre.jours_restants} jour(s) restant(s)`
              : 'Cliquez pour ouvrir immédiatement les réclamations'}
          </div>
        </div>
        <button
          onClick={toggleReclamations}
          disabled={toggling}
          className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap text-white disabled:opacity-50 ${
            fenetre?.ouvertes ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {toggling ? '...' : (fenetre?.ouvertes ? 'Fermer' : 'Ouvrir maintenant')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date de publication
          </label>
          <input
            type="datetime-local"
            value={datePub}
            onChange={(e) => setDatePub(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <p className="text-xs text-gray-500 mt-1">Vide = réclamations fermées</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Durée d'ouverture (jours)
          </label>
          <input
            type="number"
            min="1"
            max="60"
            value={jours}
            onChange={(e) => setJours(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {fenetre && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          fenetre.ouvertes
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
            : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}>
          {fenetre.ouvertes
            ? `✓ Réclamations ouvertes — ${fenetre.jours_restants} jour(s) restants (jusqu'au ${new Date(fenetre.date_limite).toLocaleString('fr-FR')})`
            : fenetre.raison === 'expire'
              ? `⏱ Délai expiré (le ${new Date(fenetre.date_limite).toLocaleString('fr-FR')})`
              : '🔒 Pas encore publié — les réclamations ne sont pas ouvertes'}
        </div>
      )}

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          msg.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        }`}>{msg.text}</div>
      )}

      <button
        onClick={save}
        disabled={saving}
        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50"
      >
        {saving ? 'Enregistrement...' : '✓ Enregistrer'}
      </button>
    </div>
  );
};
